import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Sparkles, ChevronDown } from "lucide-react";
import { TESTS } from "@/lib/siteData";
import { TEST_PARAMETERS } from "@/lib/testParameters";
import { LANDING } from "@/constants/testIds";
import { usePublicPricing } from "@/lib/usePublicPricing";

const CTA_TESTIDS = {
  basic: LANDING.pricingBasicBookBtn,
  tank: LANDING.pricingTankBookBtn,
  bore: LANDING.pricingBoreBookBtn,
};

/**
 * Per-card collapsible "View full parameters" section pulling from the
 * shared `TEST_PARAMETERS` source-of-truth so this list always matches the
 * comparison table.
 */
function ParameterDetails({ testId, featured }) {
  const [open, setOpen] = useState(false);
  const data = TEST_PARAMETERS[testId];
  if (!data) return null;
  const total = Object.values(data.groups).reduce((n, list) => n + (list?.length || 0), 0);
  return (
    <div className={`mt-5 pt-5 border-t ${featured ? "border-white/10" : "border-slate-100"}`}>
      <button
        type="button"
        data-testid={`pricing-params-toggle-${testId}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-3 text-left transition-colors ${
          featured ? "text-cyan-300 hover:text-white" : "text-cyan-700 hover:text-cyan-900"
        }`}
      >
        <span className="text-[11px] uppercase tracking-[0.18em] font-bold">
          {open ? "Hide full parameters" : `View full parameters (${total})`}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`pricing-params-panel-${testId}`} className="mt-4 space-y-3.5">
          <p className={`text-xs leading-relaxed ${featured ? "text-slate-300" : "text-slate-600"}`}>
            {data.short}
          </p>
          {Object.entries(data.groups).map(([group, items]) => (
            <div key={group}>
              <div className={`text-[10px] uppercase tracking-[0.18em] font-bold mb-1.5 ${
                featured ? "text-cyan-300" : "text-cyan-700"
              }`}>{group}</div>
              <ul className="space-y-1">
                {items.map((p) => (
                  <li key={p} className={`flex gap-1.5 text-xs leading-snug ${
                    featured ? "text-slate-200" : "text-slate-700"
                  }`}>
                    <Check className={`w-3 h-3 mt-0.5 flex-shrink-0 ${
                      featured ? "text-cyan-400" : "text-cyan-600"
                    }`} strokeWidth={3} />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const Pricing = () => {
  const { enabled: discountEnabled, pct, showStrikethrough, priceFor } = usePublicPricing();
  return (
    <section id="pricing" className="relative py-12 lg:py-16 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">
            Tests & Pricing
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.05]">
            {discountEnabled && pct > 0 ? (
              <>Prepay online and <span className="water-text">save {pct}%</span></>
            ) : (
              <>Simple, transparent <span className="water-text">water testing</span></>
            )}
          </h2>
          <p className="mt-5 text-lg text-slate-600">
            Transparent pricing. Professional analysis. Plain-English answers — every time.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
          {TESTS.map((test, i) => {
            const Icon = test.icon;
            const featured = !!test.featured;
            const live = priceFor(test.id) || {};
            const effective = live.effective ?? (discountEnabled ? test.online_prepaid : test.retail);
            const retail    = live.retail    ?? test.retail;
            const savings   = Math.max(0, retail - effective);
            return (
              <motion.div
                key={test.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className={`relative rounded-3xl p-7 lg:p-9 transition-all duration-500 hover:-translate-y-2 ${
                  featured
                    ? "bg-slate-900 text-white shadow-2xl ring-2 ring-cyan-500"
                    : "bg-white border border-slate-200 hover:shadow-2xl hover:border-cyan-300"
                }`}
              >
                {featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500 text-white text-[11px] font-bold uppercase tracking-wider shadow-lg">
                    <Sparkles className="w-3 h-3" />
                    Most Popular
                  </div>
                )}

                {/* Badge */}
                <div className="flex items-center justify-between mb-6">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                    featured ? "bg-cyan-500/20" : "bg-cyan-50"
                  }`}>
                    <Icon className={`w-6 h-6 ${featured ? "text-cyan-300" : "text-cyan-600"}`} strokeWidth={2} />
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                    featured ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-50 text-emerald-600"
                  } ${discountEnabled && pct > 0 ? "" : "invisible"}`}>
                    Save {pct}%
                  </span>
                </div>

                <div className={`text-[11px] font-bold uppercase tracking-[0.18em] mb-2 ${
                  featured ? "text-cyan-300" : "text-cyan-600"
                }`}>
                  {test.tagline}
                </div>
                <h3 className={`font-display text-2xl font-bold leading-tight ${
                  featured ? "text-white" : "text-slate-900"
                }`}>
                  {test.name}
                </h3>
                {test.short && (
                  <p className={`mt-2 text-sm leading-snug ${featured ? "text-slate-300" : "text-slate-500"}`}>
                    {test.short}
                  </p>
                )}

                {/* Price */}
                <div className="mt-6 flex items-baseline gap-3">
                  <span className={`font-display text-5xl font-bold ${featured ? "text-white" : "text-slate-900"}`}>
                    ${effective}
                  </span>
                  {discountEnabled && showStrikethrough && savings > 0 && (
                    <span className={`text-lg line-through ${featured ? "text-slate-500" : "text-slate-400"}`}>
                      ${retail}
                    </span>
                  )}
                </div>
                <div className={`text-xs mt-1 ${featured ? "text-slate-400" : "text-slate-500"}`}>
                  {discountEnabled && savings > 0
                    ? `Online prepaid · save $${savings}`
                    : "Standard price · no hidden fees"}
                </div>

                {/* Best for */}
                <div className={`mt-6 pt-6 border-t ${featured ? "border-white/10" : "border-slate-100"}`}>
                  <div className={`text-[11px] uppercase tracking-[0.18em] font-bold mb-3 ${
                    featured ? "text-slate-400" : "text-slate-500"
                  }`}>
                    Best for
                  </div>
                  <ul className="space-y-2.5">
                    {test.bestFor.map((b) => (
                      <li key={b} className="flex items-start gap-2.5">
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          featured ? "text-cyan-400" : "text-cyan-600"
                        }`} />
                        <span className={`text-sm leading-snug ${
                          featured ? "text-slate-200" : "text-slate-600"
                        }`}>
                          {b}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Highlights */}
                <div className="mt-6 flex flex-wrap gap-1.5">
                  {test.highlights.map((h) => (
                    <span key={h} className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${
                      featured
                        ? "bg-white/8 text-slate-300 border border-white/10"
                        : "bg-slate-100 text-slate-600"
                    }`}>
                      {h}
                    </span>
                  ))}
                </div>

                {/* Expandable: full parameters from shared SoT */}
                <ParameterDetails testId={test.id} featured={featured} />

                {/* CTA */}
                <Link
                  to={`/book-water-test?test=${test.id}`}
                  data-testid={CTA_TESTIDS[test.id]}
                  className={`group mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold transition-all ${
                    featured
                      ? "btn-aqua"
                      : "bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                >
                  Book Now
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-sm text-slate-500">
          Multiple samples? <Link to="/book-water-test" className="font-semibold text-cyan-600 hover:underline">Get a custom quote →</Link>
        </p>
      </div>
    </section>
  );
};
